import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  useTranslation,
  useLocale,
  SUPPORTED_LOCALES,
} from "../contexts/LocaleContext";
import { useTheme } from "../contexts/ThemeContext";
import { logout } from "../utils/auth";
import {
  Languages,
  Settings,
  Moon,
  Sun,
  FileStack,
  RefreshCw,
  Bell,
  PhoneCall,
  Mail,
  X,
  Info,
  Receipt,
  ArrowRightLeft,
  Terminal,
} from "lucide-react";

export default function SideMenu({
  isDesktop,
  collapsed,
  setCollapsed,
  mobileOpen,
  setMobileOpen,
  userRole,
}: {
  isDesktop: boolean;
  collapsed: boolean;
  setCollapsed: (v: boolean) => void;
  mobileOpen: boolean;
  setMobileOpen: (v: boolean) => void;
  userRole?: string;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const t = useTranslation();
  const { locale, setLocale } = useLocale();
  const { theme, toggleTheme } = useTheme();
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const languageDropdownRef = useRef<HTMLDivElement>(null);
  const languageButtonRef = useRef<HTMLButtonElement>(null);

  const changeLanguage = (newLocale: string) => {
    setIsLanguageDropdownOpen(false);

    // Get current path without locale
    const pathParts = location.pathname.split("/");
    const isLocalePath = SUPPORTED_LOCALES.includes(pathParts[1] as any);
    const cleanPath = isLocalePath
      ? "/" + pathParts.slice(2).join("/")
      : location.pathname;

    // Navigate to new locale path
    let newPath = "";
    if (newLocale === "cs") {
      newPath = cleanPath === "//" ? "/" : cleanPath;
    } else {
      newPath = `/${newLocale}${cleanPath === "/" ? "" : cleanPath}`;
    }

    // Force context update and navigate
    setLocale(newLocale as any);
    localStorage.setItem("app-locale", newLocale);
    navigate(newPath);
  };

  const getLocalizedPath = (path: string) => {
    if (locale === "cs") return path;
    return `/${locale}${path}`;
  };

  const items: Array<{
    key: string;
    id?: string;
    label: string;
    path: string;
    icon: React.ReactNode;
    onClick?: () => void;
  }> = [
    {
      key: "new",
      label: t.invoice.newInvoice,
      path: getLocalizedPath("/app"),
      icon: <IconPlus />,
      onClick: () => {
        try {
          localStorage.removeItem("invoice-draft-v3");
          localStorage.removeItem("invoice-internal-id");
          localStorage.setItem("skip-invoice-check", "true");
        } catch {}
      },
    },
    {
      key: "list",
      label: t.invoiceList.title,
      path: getLocalizedPath("/invoice-list"),
      icon: <IconList />,
    },
    {
      key: "notifications",
      id: "tour-notifications",
      label: (t as any).clientNotifications.title,
      path: getLocalizedPath("/client-notifications"),
      icon: <Bell className="w-5 h-5" />,
    },
    {
      key: "repeating",
      label: (t as any).repeatingInvoices.title,
      path: getLocalizedPath("/repeating-invoices"),
      icon: <RefreshCw className="w-5 h-5" />,
    },
    {
      key: "time-tracking",
      label: (t as any).timeTracking.title,
      path: getLocalizedPath("/time-tracking"),
      icon: <IconTimer />,
    },
    {
      key: "expenses",
      label: (t as any).expenses.title,
      path: getLocalizedPath("/expenses"),
      icon: <Receipt className="w-5 h-5" />,
    },
    {
      key: "stats",
      label: t.statistics.title,
      path: getLocalizedPath("/statistics"),
      icon: <IconChart />,
    },
    {
      key: "transfer",
      label: (t as any).transferInvoices.menuLabel,
      path: getLocalizedPath("/transfer-invoices"),
      icon: <ArrowRightLeft className="w-5 h-5 text-pink-500 animate-pulse" />,
    },
  ];

  if (process.env.NODE_ENV === "development") {
    items.push({
      key: "devtools",
      label: "DevTools",
      path: getLocalizedPath("/devtools"),
      icon: <Terminal className="w-5 h-5 text-amber-500" />,
    });
  }

  const handleNavigate = (path: string, onClick?: () => void) => {
    if (onClick) onClick();
    navigate(path);
    if (!isDesktop) setMobileOpen(false);
  };

  // Language options
  const languages = [
    { code: "en" as const, name: "English" },
    { code: "ru" as const, name: "Русский" },
    { code: "cs" as const, name: "Čeština" },
  ];

  // Close language dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        languageDropdownRef.current &&
        !languageDropdownRef.current.contains(event.target as Node)
      ) {
        setIsLanguageDropdownOpen(false);
      }
    };

    if (isLanguageDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isLanguageDropdownOpen]);

  // Fix text visibility on resize - ensure collapsed state is respected
  useEffect(() => {
    // Close language dropdown when menu collapses
    if (collapsed) {
      setIsLanguageDropdownOpen(false);
    }
  }, [collapsed]);

  const SidebarInner = (
    <div className="h-full flex flex-col overflow-hidden w-full dark:bg-slate-900 transition-colors duration-200">
      {/* Header with Hamburger and Logo */}
      <div
        className="w-full bg-white dark:bg-slate-900 border-b border-transparent dark:border-slate-800 transition-colors duration-200"
        style={{ width: "100%", boxSizing: "border-box" }}
      >
        {collapsed ? (
          // Collapsed: Only hamburger menu
          <div className="flex items-center justify-center px-3 py-3">
            <button
              onClick={() => {
                if (isDesktop) setCollapsed(!collapsed);
                else setMobileOpen(false);
              }}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 border border-slate-300 dark:border-slate-700 outline-none transition-colors"
              aria-label="Toggle sidebar"
            >
              <IconHamburger />
            </button>
          </div>
        ) : (
          // Expanded: Hamburger and Logo side by side
          <div className="flex items-center justify-between px-3 py-3">
            <button
              onClick={() => {
                if (isDesktop) setCollapsed(!collapsed);
                else setMobileOpen(false);
              }}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 border border-slate-300 dark:border-slate-700 outline-none transition-colors"
              aria-label="Toggle sidebar"
            >
              <IconHamburger />
            </button>
            <button
              onClick={() => {
                navigate(getLocalizedPath("/"));
                if (!isDesktop) setMobileOpen(false);
              }}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity border-0 outline-none p-0 rounded-lg px-3 py-2"
            >
              <InvoitioLogo className="h-6 w-6" />
              <span className="text-slate-700 dark:text-slate-200 font-bold text-xl">
                Invoitio
              </span>
            </button>
            <div className="w-10" /> {/* Spacer for centering */}
          </div>
        )}
      </div>
      <nav className="flex-1 py-3 overflow-auto">
        {items.map((it) => {
          const active = location.pathname === it.path;
          const isTransfer = it.key === "transfer";
          const base = collapsed ? "justify-center px-0" : "px-3";
          return (
            <button
              key={`${it.key}-${collapsed}`}
              id={(it as any).id}
              onClick={() => handleNavigate(it.path, it.onClick)}
              title={collapsed && isDesktop ? it.label : undefined}
              className={`w-full flex items-center gap-3 ${base} py-2 text-sm transition-colors border-0 outline-none bg-transparent ${
                active
                  ? "bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400"
                  : isTransfer
                  ? "text-pink-600 dark:text-pink-400 hover:bg-pink-50 dark:hover:bg-pink-900/20 font-black"
                  : "text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
              }`}
            >
              <span className="inline-flex items-center justify-center w-8">
                {it.icon}
              </span>
              {(!collapsed || (!isDesktop && mobileOpen)) && (
                <span
                  className="whitespace-normal text-left"
                  key={`label-${it.key}`}
                >
                  {it.label}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Support Section */}
      <div className="px-3 mb-2">
        {(!collapsed || (!isDesktop && mobileOpen)) && (
          <p className="text-[9px] font-bold text-slate-400 dark:text-slate-500 mb-2 leading-tight px-1 uppercase tracking-wider">
            {t.support.miniLabel}
          </p>
        )}
        <button
          onClick={() => setIsSupportModalOpen(true)}
          title={collapsed && isDesktop ? t.support.button : undefined}
          className={`w-full flex items-center gap-3 ${
            collapsed ? "justify-center px-0" : "px-3"
          } py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl shadow-lg shadow-indigo-500/25 transition-all duration-200 group border-0 outline-none`}
        >
          <span className="inline-flex items-center justify-center w-8">
            <PhoneCall className="w-5 h-5 group-hover:scale-110 transition-transform" />
          </span>
          {(!collapsed || (!isDesktop && mobileOpen)) && (
            <span className="text-xs font-black tracking-widest whitespace-normal text-left">
              {t.support.button}
            </span>
          )}
        </button>
      </div>

      <div className="p-3 border-t border-slate-200 dark:border-slate-800 space-y-2 transition-colors duration-200">
        {/* Theme Toggle */}
        <button
          onClick={toggleTheme}
          title={
            collapsed && isDesktop
              ? theme === "light"
                ? "Dark Mode"
                : "Light Mode"
              : undefined
          }
          className={`w-full flex items-center gap-3 ${
            collapsed ? "justify-center px-0" : "px-3"
          } py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg border-0 outline-none transition-colors bg-transparent`}
        >
          <span className="inline-flex items-center justify-center w-8">
            {theme === "light" ? (
              <Moon className="w-5 h-5" />
            ) : (
              <Sun className="w-5 h-5" />
            )}
          </span>
          {(!collapsed || (!isDesktop && mobileOpen)) && (
            <span className="whitespace-normal text-left" key="theme-label">
              {theme === "light" ? "Dark Mode" : "Light Mode"}
            </span>
          )}
        </button>

        {/* Language */}
        <div className="relative" ref={languageDropdownRef}>
          <button
            ref={languageButtonRef}
            onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
            title={
              collapsed && isDesktop
                ? t.common.language || "Language"
                : undefined
            }
            className={`w-full flex items-center gap-3 ${
              collapsed ? "justify-center px-0" : "px-3"
            } py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg border-0 outline-none transition-colors bg-transparent`}
          >
            <span className="inline-flex items-center justify-center w-8">
              <Languages className="w-5 h-5" />
            </span>
            {(!collapsed || (!isDesktop && mobileOpen)) && (
              <span
                className="whitespace-normal text-left"
                key="language-label"
              >
                {t.common.language || "Language"}
              </span>
            )}
          </button>
          {isLanguageDropdownOpen && (
            <>
              {collapsed && isDesktop ? (
                <div
                  className="fixed bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-[100] min-w-[180px]"
                  style={{
                    left: languageButtonRef.current
                      ? languageButtonRef.current.getBoundingClientRect()
                          .right +
                        8 +
                        "px"
                      : "72px",
                    top: languageButtonRef.current
                      ? languageButtonRef.current.getBoundingClientRect().top +
                        "px"
                      : "0px",
                  }}
                >
                  <div className="p-2">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => changeLanguage(lang.code)}
                        className={`w-full text-left px-2 py-1.5 text-sm rounded-md transition-colors ${
                          locale === lang.code
                            ? "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 font-medium"
                            : "text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <Languages className="w-4 h-4" />
                          <span>{lang.name}</span>
                          {locale === lang.code && (
                            <span className="ml-auto text-indigo-600 dark:text-indigo-400">
                              ✓
                            </span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="absolute bottom-full left-0 mb-2 w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-50">
                  <div className="p-2">
                    <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 px-2 py-1 mb-1">
                      {t.common.language || "Language"}
                    </div>
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => changeLanguage(lang.code)}
                        className={`w-full text-left px-2 py-1.5 text-sm rounded-md transition-colors ${
                          locale === lang.code
                            ? "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 font-medium"
                            : "text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <Languages className="w-4 h-4" />
                          <span>{lang.name}</span>
                          {locale === lang.code && (
                            <span className="ml-auto text-indigo-600 dark:text-indigo-400">
                              ✓
                            </span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        {/* User Settings */}
        <button
          onClick={() => handleNavigate(getLocalizedPath("/settings"))}
          title={
            collapsed && isDesktop ? t.common.settings || "Settings" : undefined
          }
          className={`w-full flex items-center gap-3 ${
            collapsed ? "justify-center px-0" : "px-3"
          } py-2 text-sm transition-colors border-0 outline-none bg-transparent ${
            location.pathname === getLocalizedPath("/settings")
              ? "bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400"
              : "text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
          } rounded-lg`}
        >
          <span className="inline-flex items-center justify-center w-8">
            <Settings className="w-5 h-5" />
          </span>
          {(!collapsed || (!isDesktop && mobileOpen)) && (
            <span className="whitespace-normal text-left" key="settings-label">
              {t.common.settings || "Settings"}
            </span>
          )}
        </button>
        {/* Logout */}
        <button
          onClick={async () => {
            await logout();
            navigate("/");
          }}
          title={
            collapsed && isDesktop ? t.common.logout || "Logout" : undefined
          }
          className={`w-full flex items-center gap-3 ${
            collapsed ? "justify-center px-0" : "px-3"
          } py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg border-0 outline-none transition-colors bg-transparent`}
        >
          <span className="inline-flex items-center justify-center w-8">
            <IconLogout />
          </span>
          {(!collapsed || (!isDesktop && mobileOpen)) && (
            <span className="whitespace-normal text-left" key="logout-label">
              {t.common.logout || "Logout"}
            </span>
          )}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop sidebar */}
      {isDesktop && (
        <motion.aside
          initial={false}
          animate={{ width: collapsed ? 72 : 240 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="hidden lg:flex h-screen sticky top-0 left-0 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden transition-colors duration-200"
          style={{ zIndex: 120 }}
        >
          {SidebarInner}
        </motion.aside>
      )}

      {/* Mobile/Tablet drawer */}
      {!isDesktop && (
        <>
          <AnimatePresence>
            {mobileOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/40 backdrop-blur-sm"
                style={{ zIndex: 140 }}
                onClick={() => setMobileOpen(false)}
              />
            )}
          </AnimatePresence>
          <AnimatePresence>
            {mobileOpen && (
              <motion.aside
                initial={{ x: -280 }}
                animate={{ x: 0 }}
                exit={{ x: -280 }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                className="fixed top-0 left-0 h-full w-[260px] bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 shadow-xl overflow-hidden transition-colors duration-200"
                style={{ zIndex: 150 }}
                onClick={(e) => e.stopPropagation()}
              >
                {SidebarInner}
              </motion.aside>
            )}
          </AnimatePresence>
        </>
      )}

      {/* Support Modal */}
      <AnimatePresence>
        {isSupportModalOpen && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsSupportModalOpen(false)}
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-md bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl overflow-hidden border border-slate-200 dark:border-slate-800"
            >
              {/* Header Gradient */}
              <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />

              <button
                onClick={() => setIsSupportModalOpen(false)}
                className="absolute top-6 right-6 p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-slate-400 border-0 outline-none bg-transparent"
              >
                <X size={20} />
              </button>

              <div className="p-8 md:p-10">
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900/30 rounded-2xl flex items-center justify-center mb-6 text-indigo-600 dark:text-indigo-400 shadow-inner group">
                    <PhoneCall
                      size={32}
                      className="group-hover:scale-110 transition-transform"
                    />
                  </div>

                  <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-2 tracking-tight">
                    {t.support.modalTitle}
                  </h3>
                  <p className="text-slate-500 dark:text-slate-400 font-medium mb-8 text-sm">
                    {t.support.modalSubtitle}
                  </p>

                  <div className="w-full bg-slate-50 dark:bg-slate-900/50 rounded-3xl p-6 border border-slate-100 dark:border-slate-800 mb-6">
                    <div className="flex items-center justify-center gap-2 text-indigo-600 dark:text-indigo-400 font-black text-[10px] uppercase tracking-[0.2em] mb-3">
                      <Mail size={12} />
                      {t.support.modalEmailLabel}
                    </div>
                    <a
                      href="mailto:support@invoitio.com"
                      className="text-xl md:text-2xl font-bold text-slate-900 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors no-underline"
                    >
                      support@invoitio.com
                    </a>
                  </div>

                  <div className="flex items-start gap-3 text-left bg-amber-50 dark:bg-amber-900/20 p-4 rounded-2xl border border-amber-100 dark:border-amber-900/30">
                    <Info
                      size={18}
                      className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5"
                    />
                    <p className="text-xs font-medium text-amber-800 dark:text-amber-300 leading-relaxed">
                      {t.support.modalNote}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}

function IconHamburger() {
  return (
    <svg
      className="w-5 h-5 text-slate-700 dark:text-slate-300"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M4 6h16M4 12h16M4 18h16"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}
function IconList() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
      <path
        d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}
function IconTimer() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path
        d="M12 6v6l4 2"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function IconPlus() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 5v14M5 12h14"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}
function IconChart() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
      <path
        d="M4 20h16M7 16v-6M12 16V8M17 16v-9"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}
function IconLogout() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
      <path
        d="M15 7l5 5-5 5M10 12h10M15 3H6a2 2 0 00-2 2v14a2 2 0 002 2h9"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function InvoitioLogo({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="3"
        y="4"
        width="18"
        height="16"
        rx="3"
        className="fill-emerald-400"
      />
      <path d="M17 4v4h4" className="fill-emerald-500" />
      <circle cx="10" cy="11" r="1" className="fill-slate-800" />
      <circle cx="14" cy="11" r="1" className="fill-slate-800" />
      <path
        d="M9.5 14c1.2 1.2 3.8 1.2 5 0"
        stroke="#0f172a"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  );
}
